import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Route, RouteComponentProps, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  isTokenExpired,
  isUserRepresentative,
  redirectToCorrectLocation,
  setupClient,
  startCiamLogin,
} from "../../utils/authentication";
import { RepresentativeMainNavigation } from "../../components/RepresentativeMainNavigation";
import RepresentativeTermsGate from "../../components/RepresentativeTermsGate";
import { ConnectedRepresentativeProfileGate } from "../../components/RepresentativeProfileGate";
import { FullscreenLoading } from "../../components/FullscreenLoading";
import { RepresentativeRouteProps } from "./types";

type AuthStatus = "loading" | "loggedIn" | "loggedOut" | "redirectingToCiam";

// Route guard for /representative/* paths. Blocks with the Terms of Use gate
// until the buyer accepts. Deposit-detail deep links redirect through CIAM so
// the target URL survives login; auth is evaluated once on mount.
export const RepresentativeRoute = ({
  component: Component,
  path,
  exact,
  emptyLayout,
}: RepresentativeRouteProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const ciamRedirectTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const ciamAccessToken = localStorage.getItem("ciam_access_token");
    const userProfile = localStorage.getItem("user_profile");
    const expiryDate = new Date(localStorage.getItem("expiry_date") ?? 0);

    if (!ciamAccessToken || !userProfile || isTokenExpired(expiryDate)) {
      setStatus("loggedOut");
      // Store the current URL so we can redirect back after CIAM login.
      // Currently only deposit detail URLs are preserved; this check can be
      // removed if we want to redirect after login for every possible URL.
      const pathname = window.location.pathname;
      if (/^\/representative\/market\/deposit\/[^/]+$/.test(pathname)) {
        localStorage.setItem("post_login_redirect", pathname);
        setStatus("redirectingToCiam");
        ciamRedirectTimeout.current = setTimeout(() => {
          startCiamLogin();
        }, 3000);
      } else {
        history.push("/login");
      }
    } else if (!isUserRepresentative(userProfile)) {
      redirectToCorrectLocation(userProfile, history);
    } else {
      setupClient(ciamAccessToken);
      setStatus("loggedIn");
    }

    return () => {
      if (ciamRedirectTimeout.current) {
        clearTimeout(ciamRedirectTimeout.current);
      }
    };
  }, [history]);

  if (status === "redirectingToCiam") {
    return <FullscreenLoading isVisible>{t("route.qrCodeScanned.title")}</FullscreenLoading>;
  }

  if (status === "loading") {
    return <FullscreenLoading isVisible />;
  }

  if (status === "loggedIn") {
    // Component is widened to Route's own (untyped) component slot; narrow it
    // back to the emptyLayout-aware shape the routed pages actually receive.
    const RoutedComponent = Component as React.ComponentType<
      RouteComponentProps & { emptyLayout?: boolean }
    >;
    const route = (
      <Route
        key={path}
        exact={exact}
        path={path}
        render={routeProps => <RoutedComponent {...routeProps} emptyLayout={emptyLayout} />}
      />
    );

    return (
      <>
        <RepresentativeTermsGate />
        <ConnectedRepresentativeProfileGate />
        {emptyLayout ? route : <RepresentativeMainNavigation>{route}</RepresentativeMainNavigation>}
      </>
    );
  }

  return <FullscreenLoading isVisible />;
};
